import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { BookOpen, Clock, Info, MessageCircle, Lightbulb } from 'lucide-react';
import { tokenizeAll, temporalMarkers, narrativePhases, splitIntoSentences, countWordMatches, generateAPAInterpretation } from './qualitativeUtils';

const NarrativeAnalysis = ({ texts }) => {
  const analysis = useMemo(() => {
    const phaseResults = Object.entries(narrativePhases).map(([key, phase]) => {
      const markerSet = new Set(phase.markers);
      let totalMatches = 0;
      const matchingResponses = [];

      texts.forEach((text, idx) => {
        const words = tokenizeAll(text);
        const matches = countWordMatches(words, markerSet);
        if (matches > 0) {
          totalMatches += matches;
          matchingResponses.push({ text, matches, index: idx });
        }
      });

      return {
        id: key,
        name: phase.name,
        description: phase.description,
        count: totalMatches,
        responseCount: matchingResponses.length,
        percentage: texts.length > 0 ? Math.round((matchingResponses.length / texts.length) * 100) : 0,
        examples: matchingResponses.sort((a, b) => b.matches - a.matches).slice(0, 2)
      };
    });

    let temporalCount = 0;
    const timelineResponses = [];

    texts.forEach((text, idx) => {
      const words = tokenizeAll(text);
      const matches = countWordMatches(words, temporalMarkers);
      if (matches > 0) {
        temporalCount++;
        timelineResponses.push({ 
          text, 
          matches, 
          markers: words.filter(w => temporalMarkers.has(w))
        });
      }
    });

    const sentenceLengths = texts.map(text => {
      const sentences = splitIntoSentences(text);
      return {
        count: sentences.length,
        avgLength: sentences.length > 0 
          ? Math.round(sentences.reduce((sum, s) => sum + s.split(/\s+/).length, 0) / sentences.length)
          : 0
      };
    });

    const avgSentences = sentenceLengths.length > 0
      ? Math.round(sentenceLengths.reduce((sum, s) => sum + s.count, 0) / sentenceLengths.length)
      : 0;

    const dominantPhase = phaseResults.sort((a, b) => b.count - a.count)[0];

    const firstPersonCount = texts.filter(text => {
      const words = tokenizeAll(text);
      return words.some(w => ['i', 'me', 'my', 'myself', 'mine'].includes(w));
    }).length;

    return {
      phases: phaseResults,
      temporalCount,
      temporalPercentage: texts.length > 0 ? Math.round((temporalCount / texts.length) * 100) : 0,
      timelineExamples: timelineResponses.slice(0, 3),
      avgSentences,
      totalResponses: texts.length,
      dominantPhase: dominantPhase?.name || 'None',
      phasePercentage: dominantPhase?.percentage || 0,
      firstPersonPercentage: texts.length > 0 ? Math.round((firstPersonCount / texts.length) * 100) : 0
    };
  }, [texts]);

  const interpretation = useMemo(() => 
    generateAPAInterpretation('narrative', analysis),
    [analysis]
  );

  const narrativeSummary = useMemo(() => {
    const dominantPhase = analysis.phases.sort((a, b) => b.count - a.count)[0];
    const secondPhase = analysis.phases.sort((a, b) => b.count - a.count)[1];
    
    return `This narrative analysis examined ${analysis.totalResponses} text segments for structural storytelling elements and temporal patterns. The analysis identified ${analysis.temporalPercentage}% of responses containing temporal markers (words like "first," "then," "finally"), indicating the presence of chronological narrative structure. ${analysis.firstPersonPercentage}% of responses employed first-person voice, suggesting personal experiential accounts. The dominant narrative phase was "${dominantPhase?.name || 'Orientation'}" (${dominantPhase?.percentage || 0}% presence), followed by "${secondPhase?.name || 'Evaluation'}" (${secondPhase?.percentage || 0}%). Responses averaged ${analysis.avgSentences} sentences each, providing ${analysis.avgSentences > 3 ? 'substantial narrative depth' : 'concise narrative accounts'}. The narrative structure radar reveals how participants construct their stories, with ${dominantPhase?.name === 'Complication' ? 'emphasis on challenges and problems' : dominantPhase?.name === 'Resolution' ? 'focus on outcomes and solutions' : 'orientation toward setting context'}. Researchers should examine how these narrative patterns relate to participant experiences and consider whether story structures differ across participant groups or topics. Triangulating with thematic analysis may reveal how narrative elements connect to broader themes.`;
  }, [analysis]);

  const radarData = analysis.phases.map(p => ({
    phase: p.name,
    presence: p.percentage,
    fullMark: 100
  }));

  return (
    <div className="narrative-analysis">
      <div className="analysis-header">
        <h4><BookOpen size={20} /> Narrative Analysis</h4>
        <p>Examine story structure, temporal flow, and narrative elements</p>
      </div>

      <div className="analysis-guidance">
        <Lightbulb size={18} />
        <p>Here are some charts/graphs and some analytical content to get you started. You will need to expand on these and possibly make connections and triangulation with other data analysis.</p>
      </div>

      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-value">{analysis.totalResponses}</div>
          <div className="stat-label">Narratives Analyzed</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{analysis.temporalPercentage}%</div>
          <div className="stat-label">With Temporal Markers</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{analysis.firstPersonPercentage}%</div>
          <div className="stat-label">First-Person Voice</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{analysis.avgSentences}</div>
          <div className="stat-label">Avg Sentences/Response</div>
        </div>
      </div>

      <div className="chart-section">
        <h5>Narrative Structure Radar</h5>
        <p className="chart-desc">Presence of different narrative phases across responses</p>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={radarData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="phase" tick={{ fontSize: 11 }} />
            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
            <Radar
              name="Phase Presence"
              dataKey="presence"
              stroke="#6366f1"
              fill="#6366f1"
              fillOpacity={0.5}
            />
            <Tooltip formatter={(value) => [`${value}%`, 'Presence']} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="phases-detail">
        <h5><MessageCircle size={16} /> Narrative Phases</h5>
        {analysis.phases.map(phase => (
          <div key={phase.id} className="phase-card">
            <div className="phase-header">
              <span className="phase-name">{phase.name}</span>
              <span className="phase-count">{phase.count} occurrences ({phase.percentage}%)</span>
            </div>
            <div className="phase-desc">{phase.description}</div>
            <div className="phase-bar">
              <div 
                className="phase-fill" 
                style={{ width: `${Math.min(phase.percentage, 100)}%` }}
              />
            </div>
            {phase.examples.length > 0 && (
              <div className="phase-examples">
                <span className="example-label">Example:</span>
                <span className="example-text">
                  "{phase.examples[0].text.substring(0, 100)}..."
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="chart-section">
        <h5><Clock size={16} /> Temporal Markers Distribution</h5>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart 
            data={analysis.phases} 
            margin={{ left: 20, right: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis />
            <Tooltip formatter={(value) => [`${value}`, 'Count']} />
            <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {analysis.timelineExamples.length > 0 && (
        <div className="timeline-examples">
          <h5>Responses with Strong Temporal Structure</h5>
          {analysis.timelineExamples.map((ex, idx) => (
            <div key={idx} className="timeline-example">
              <div className="markers-found">
                Temporal markers: {ex.markers.slice(0, 5).join(', ')}
              </div>
              <p>"{ex.text.substring(0, 200)}..."</p>
            </div>
          ))}
        </div>
      )}

      <div className="interpretation-section">
        <h5><Info size={18} /> Analytical Summary</h5>
        <p>{narrativeSummary}</p>
      </div>
    </div>
  );
};

export default NarrativeAnalysis;
